import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Badge} from '@/components/ui/badge'
import {Pagination} from '@/components/ui/pagination'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {ErrorBanner} from '@/components/ui/error-banner'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {formatTimestamp} from '@/lib/date-utils'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {MutedText} from '@/components/shared/MutedText'
import {TruncatedCell} from '@/components/shared/TruncatedCell'
import {PageLayout} from '@/components/shared/PageLayout'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {useIsEmbedded} from '@/components/shared/EmbeddedContext'
import {LlmPromptTemplateControllerListEndpoint} from '@/types/endpoint-contracts'
import {CONCEPT_ICON} from '@/lib/concepts'

export function LlmPrompts() {
    const i18n = useI18n()
    const services = useServices()
    const embedded = useIsEmbedded()
    usePageTitle(embedded ? undefined : i18n.llmPrompts.pageTitle)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: LlmPromptTemplateControllerListEndpoint,
        sortable: true,
        defaultSortBy: LlmPromptTemplateControllerListEndpoint.sort.KEY,
        defaultSortDir: SortDir.ASC,
    })

    const {data: templatesPage, isLoading, error} = useQuery({
        queryKey: ['llm-prompts', queryString, page, pageSize],
        queryFn: () => services.llmPrompts.list(
            Object.fromEntries(new URLSearchParams(queryString))),
    })

    const templates = templatesPage?.content ?? []
    const totalItems = templatesPage?.totalElements ?? 0
    const totalPages = templatesPage?.totalPages ?? 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    if (error) {
        return <ErrorBanner error={error}/>
    }

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.llmPrompt}
            title={i18n.llmPrompts.title}
            count={totalItems}
            data-testid="llm-prompts-list"
            filters={
                <UnifiedSearchBar
                    contract={LlmPromptTemplateControllerListEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.llmPrompts.searchPlaceholder}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            {isLoading && (
                <MutedText>{i18n.common.loading}</MutedText>
            )}
            <Table data-testid="llm-prompts-table">
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-key">{i18n.llmPrompts.columns.key}</TableHead>
                        <TableHead data-testid="col-display-name">{i18n.llmPrompts.columns.displayName}</TableHead>
                        <TableHead data-testid="col-description">{i18n.llmPrompts.columns.description}</TableHead>
                        <TableHead data-testid="col-last-updated">{i18n.llmPrompts.columns.lastUpdated}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {templates.map((template) => (
                        <ClickableTableRow
                            key={template.id}
                            to={routes.admin.llmPrompts.detail(template.key)}
                            data-testid={`llm-prompt-row-${template.key}`}
                        >
                            <TableCell>
                                <Badge variant="outline" className="font-mono">{template.key}</Badge>
                            </TableCell>
                            <TableCell className="font-medium">{template.displayName}</TableCell>
                            <TruncatedCell className="text-muted-foreground">
                                {template.description}
                            </TruncatedCell>
                            <TableCell>
                                <MutedText>{formatTimestamp(template.updatedAt)}</MutedText>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {!isLoading && templates.length === 0 && (
                        <TableEmptyState
                            colSpan={4}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.llmPrompts.noMatchingPrompts}
                            emptyMessage={i18n.llmPrompts.emptyState}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
